import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import MetricCard from './MetricCard';
import { API_BASE_URL, TENANT_ID } from '../utils/api';

interface Job {
  id: string;
  name: string;
  data: any;
  status: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed';
  progress: number;
  timestamp: string;
  processedOn?: string;
  finishedOn?: string;
  failedReason?: string;
  queueName: string;
}

const JobBrowser: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [queueNames, setQueueNames] = useState<string[]>([]);
  const [queueTypes, setQueueTypes] = useState<string[]>([]);
  const [selectedQueueMetrics, setSelectedQueueMetrics] = useState<any | null>(null);
  const [filters, setFilters] = useState({
    status: 'all',
    queueName: 'all',
    queueType: 'all',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });
  const { token } = useAuth();
  const { addToast } = useToast();

  const [dlqStats, setDlqStats] = useState<{ waiting: number; total: number } | null>(null);

  useEffect(() => {
    // load queues list and DLQ stats once
    loadQueueOptions();
    fetchDlqStats();
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [filters, pagination.page]);

  useEffect(() => {
    if (filters.queueName && filters.queueName !== 'all') {
      fetchQueueMetrics(filters.queueName);
    } else {
      setSelectedQueueMetrics(null);
    }
  }, [filters.queueName]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const start = (pagination.page - 1) * pagination.limit;
      const end = pagination.page * pagination.limit;
      const params = new URLSearchParams({ start: start.toString(), end: end.toString() });
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);
      if ((!filters.queueName || filters.queueName === 'all') && filters.queueType && filters.queueType !== 'all') {
        params.set('queueType', filters.queueType);
      }

      const url = (!filters.queueName || filters.queueName === 'all')
        ? `${API_BASE_URL}/admin/monitoring/jobs?${params}`
        : `${API_BASE_URL}/admin/monitoring/queues/${filters.queueName}/jobs?${params}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
        },
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(Array.isArray(data.jobs) ? data.jobs : []);
        setPagination(prev => ({
          ...prev,
          total: (typeof data.pagination?.total === 'number')
            ? data.pagination.total
            : (start + ((data.jobs?.length || 0)) + ((data.jobs?.length === pagination.limit) ? pagination.limit : 0)),
        }));
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadQueueOptions = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/monitoring/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        const names: string[] = (data.queueMetrics || []).map((m: any) => m.queueName || m.name).filter(Boolean);
        const unique = Array.from(new Set(names));
        setQueueNames(unique);

        const inferType = (n: string) => (n?.split(':')[0] || n?.split('-')[0] || n || '').toLowerCase();
        const types = Array.from(new Set(unique.map(inferType))).filter(Boolean);
        setQueueTypes(types);
      }
    } catch (e) {
      console.error('Error loading queue options:', e);
    }
  };

  const fetchQueueMetrics = async (queueName: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/monitoring/queues/${queueName}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedQueueMetrics(data);
      } else {
        setSelectedQueueMetrics(null);
      }
    } catch (e) {
      console.error('Error fetching queue metrics:', e);
      setSelectedQueueMetrics(null);
    }
  };

  // Acciones operativas sobre colas
  const handleRetryFailedJobs = async (queueName: string) => {
    if (!queueName || queueName === 'all') return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/monitoring/queues/${queueName}/retry-failed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ limit: 100 }),
      });
      const data = await res.json();
      if (res.ok) {
        addToast({
          type: 'success',
          title: 'Reintento exitoso',
          message: data.message || `Jobs fallidos reintentados en ${queueName}`,
        });
        await fetchJobs();
        await fetchQueueMetrics(queueName);
      } else {
        addToast({ type: 'error', title: 'Error', message: data.error || 'No se pudieron reintentar los jobs' });
      }
    } catch (err: any) {
      addToast({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCleanQueue = async (queueName: string, status: 'completed' | 'failed') => {
    if (!queueName || queueName === 'all') return;
    if (!confirm(`¿Estás seguro de limpiar todos los jobs ${status}s en ${queueName}?`)) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/monitoring/queues/${queueName}/clean`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (res.ok) {
        addToast({ type: 'success', title: 'Cola limpia', message: data.message || `Limpieza de ${status}s completada` });
        await fetchJobs();
        await fetchQueueMetrics(queueName);
      } else {
        addToast({ type: 'error', title: 'Error', message: data.error || 'Fallo al limpiar la cola' });
      }
    } catch (err: any) {
      addToast({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteJob = async (queueName: string, jobId: string) => {
    if (!confirm(`¿Eliminar el job #${jobId} de la cola ${queueName}?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/monitoring/queues/${queueName}/jobs/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        addToast({ type: 'info', title: 'Job eliminado', message: data.message || `Job #${jobId} eliminado` });
        await fetchJobs();
      } else {
        addToast({ type: 'error', title: 'Error', message: data.error || 'Fallo al eliminar job' });
      }
    } catch (err: any) {
      addToast({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const fetchDlqStats = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/dlq/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setDlqStats(data);
      }
    } catch {
      // Ignorar fallos de red silenciosos
    }
  };

  const handleReprocessDlq = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/dlq/reprocess?limit=50`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
        },
      });
      const data = await res.json();
      if (res.ok) {
        addToast({
          type: 'success',
          title: 'DLQ Reprocesada',
          message: `Se reencolaron ${data.reprocessed} mensajes de la Dead Letter Queue.`,
        });
        await fetchDlqStats();
        await fetchJobs();
      } else {
        addToast({ type: 'error', title: 'Error DLQ', message: data.error || 'Fallo al reprocesar DLQ' });
      }
    } catch (err: any) {
      addToast({ type: 'error', title: 'Error DLQ', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'waiting': return '⏳';
      case 'active': return '⚡';
      case 'completed': return '✅';
      case 'failed': return '❌';
      case 'delayed': return '⏰';
      default: return '❓';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('es-ES');
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="job-browser">
      <div className="page-header">
        <h2>📋 Navegador de Jobs</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {dlqStats && dlqStats.total > 0 && (
            <button
              onClick={handleReprocessDlq}
              disabled={actionLoading}
              className="refresh-button"
              style={{ backgroundColor: '#ea580c' }}
            >
              ⚠️ Reprocesar DLQ ({dlqStats.waiting})
            </button>
          )}
          <button onClick={() => { fetchJobs(); fetchDlqStats(); }} className="refresh-button">
            🔄 Actualizar
          </button>
        </div>
      </div>

      {/* Banner de Advertencia de DLQ */}
      {dlqStats && dlqStats.total > 0 && (
        <div style={{ backgroundColor: '#fff7ed', border: '1px solid #fdba74', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontSize: '0.85rem', color: '#c2410c' }}>
            <strong>⚠️ Mensajes descartados en Cola de Descarte (DLQ):</strong> Hay {dlqStats.waiting} mensajes que agotaron sus reintentos automáticos.
          </div>
          <button
            onClick={handleReprocessDlq}
            disabled={actionLoading}
            style={{ padding: '6px 14px', fontSize: '0.8rem', fontWeight: 'bold', borderRadius: 8, backgroundColor: '#ea580c', color: '#ffffff', border: 'none', cursor: 'pointer' }}
          >
            {actionLoading ? 'Reprocesando...' : '▶ Reprocesar lote de 50'}
          </button>
        </div>
      )}

      {/* Filtros */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Estado:</label>
          <select
            value={filters.status}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, status: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <option value="all">Todos</option>
            <option value="waiting">Esperando</option>
            <option value="active">Activos</option>
            <option value="completed">Completados</option>
            <option value="failed">Fallidos</option>
            <option value="delayed">Retrasados</option>
          </select>
        </div>

        {/* Selector de Cola */}
        <div className="filter-group">
          <label>Cola:</label>
          <select
            value={filters.queueName}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, queueName: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <option value="all">Todas las colas</option>
            {queueNames.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>

        {/* Tipo de Cola: visible solo cuando se ven todas las colas */}
        {(!filters.queueName || filters.queueName === 'all') && (
          <div className="filter-group">
            <label>Tipo de Cola:</label>
            <select
              value={filters.queueType}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, queueType: e.target.value }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="all">Todos</option>
              {queueTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Barra de Acciones Operativas de la Cola */}
      {filters.queueName && filters.queueName !== 'all' && (
        <div className="chart-container" style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <strong style={{ fontSize: '0.9rem' }}>Acciones para {filters.queueName}:</strong>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="refresh-button"
              disabled={actionLoading}
              onClick={() => handleRetryFailedJobs(filters.queueName)}
            >
              🔄 Reintentar fallidos
            </button>
            <button
              className="refresh-button"
              style={{ backgroundColor: '#475569' }}
              disabled={actionLoading}
              onClick={() => handleCleanQueue(filters.queueName, 'completed')}
            >
              🧹 Limpiar completados
            </button>
            <button
              className="refresh-button"
              style={{ backgroundColor: '#dc2626' }}
              disabled={actionLoading}
              onClick={() => handleCleanQueue(filters.queueName, 'failed')}
            >
              🗑️ Limpiar fallidos
            </button>
          </div>
        </div>
      )}

      {/* Resumen de métricas de la cola seleccionada */}
      {selectedQueueMetrics && (
        <div className="metrics-grid">
          <MetricCard title="Activos" value={selectedQueueMetrics.active || 0} icon="⚡" />
          <MetricCard title="Esperando" value={selectedQueueMetrics.waiting || 0} icon="⏳" type="warning" />
          <MetricCard title="Completados" value={selectedQueueMetrics.completed || 0} icon="✅" type="success" />
          <MetricCard title="Fallidos" value={selectedQueueMetrics.failed || 0} icon="❌" type="error" />
        </div>
      )}

      {/* Lista de Jobs */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando jobs...</p>
        </div>
      ) : (
        <>
          <div className="jobs-list">
            {jobs.length === 0 ? (
              <div className="no-jobs">
                <p>No se encontraron jobs con los filtros seleccionados</p>
              </div>
            ) : (
              jobs.map((job) => (
                <div key={job.id} className={`job-item job-${job.status}`}>
                  <div className="job-header">
                    <span className="job-status">
                      {getStatusIcon(job.status)} {job.status}
                    </span>
                    <span className="job-id">ID: {job.id}</span>
                    <span className="job-queue">Cola: {job.queueName}</span>
                  </div>
                  
                  <div className="job-details">
                    <h4 className="job-name">{job.name}</h4>
                    
                    {job.progress > 0 && (
                      <div className="job-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${job.progress}%` }}
                          ></div>
                        </div>
                        <span className="progress-text">{job.progress}%</span>
                      </div>
                    )}
                    
                    <div className="job-timestamps">
                      <span>Creado: {formatTimestamp(job.timestamp)}</span>
                      {job.processedOn && (
                        <span>Procesado: {formatTimestamp(job.processedOn)}</span>
                      )}
                      {job.finishedOn && (
                        <span>Finalizado: {formatTimestamp(job.finishedOn)}</span>
                      )}
                    </div>
                    
                    {job.failedReason && (
                      <div className="job-error">
                        <strong>Error:</strong> {job.failedReason}
                      </div>
                    )}
                    
                    <details className="job-data">
                      <summary>Ver datos del job</summary>
                      <pre>{JSON.stringify(job.data, null, 2)}</pre>
                    </details>

                    <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        style={{ padding: '4px 10px', fontSize: '0.75rem', borderRadius: 6, border: '1px solid #ef4444', backgroundColor: 'transparent', color: '#ef4444', cursor: 'pointer' }}
                        onClick={() => handleDeleteJob(job.queueName, job.id)}
                      >
                        🗑️ Eliminar job
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                ← Anterior
              </button>
              
              <span className="pagination-info">
                Página {pagination.page} de {totalPages} ({pagination.total} jobs)
              </span>
              
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === totalPages}
              >
                Siguiente →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default JobBrowser;
